// =============================================================================
// hooks/simulator/useSendSimulation.ts — Mutation de envio de simulação por
// WhatsApp (F14-S06).
//
// POST /api/simulations/:id/send
//   Header obrigatório: Idempotency-Key (UUID v4)
//   Response: { status: 'sent' | 'already_sent', sent_message_id: string | null }
//
// Idempotência: a cada chamada send() geramos um UUID novo. O botão fica
// desabilitado (isPending=true) enquanto a requisição está em voo — isso
// evita duplo clique sem precisar de lógica adicional no componente.
//
// Erros mapeados (espelham os erros tipados do backend — F14-S05, revisado F50-S08):
//   403 FEATURE_DISABLED           → FLAG_DISABLED (flag simulations.send.enabled desligada)
//   403 FORBIDDEN                  → FORBIDDEN (sem permissão simulations:send)
//   422 details.code=lead_no_phone → NO_PHONE (lead sem telefone E.164)
//   422 details.code=TEMPLATE_CATEGORY_NOT_ALLOWED → TEMPLATE_CATEGORY_NOT_ALLOWED
//   422 outro/desconhecido         → UNKNOWN (mensagem do servidor, nunca "sem telefone")
//   409 details.code=CONTACT_BLOCKED         → CONTACT_BLOCKED ("não me contate")
//   409 details.code=IDEMPOTENCY_IN_PROGRESS → SEND_IN_PROGRESS (envio já em voo)
//   409 outro/desconhecido         → UNKNOWN (mensagem do servidor)
//   502                            → META_UNAVAILABLE (Meta não configurada / fora do ar)
//   outro                          → UNKNOWN
//
// F50-S08 — CONTRATO DE ERRO (lido do código real, não presumido):
//   O error handler do Fastify (apps/api/src/app.ts:532-539) monta o corpo
//   `{ error: AppError.code, message: AppError.message, details?: AppError.details }`.
//   Não existe campo `code` no JSON — só `error`. Por isso `ApiError.code`
//   (populado em apps/web/src/lib/api.ts:168 a partir de `body.code`, que a
//   API nunca envia) fica sempre `'HTTP_ERROR'` para qualquer resposta de
//   AppError — não dá para classificar por `err.code` (exceto o ramo 403
//   abaixo, que já não funcionava antes deste slot e não faz parte do seu
//   escopo). O código de domínio real vem em `err.details.code`:
//     - lead sem telefone: apps/api/src/modules/simulations/service.ts:950-956
//       → 422 VALIDATION_ERROR, details: { code: 'lead_no_phone' }
//     - categoria de template não permitida:
//       apps/api/src/modules/conversations/template-catalog.service.ts:118-131
//       → 422 VALIDATION_ERROR, details: { code: 'TEMPLATE_CATEGORY_NOT_ALLOWED', ... }
//     - contato bloqueado ("não me contate"):
//       apps/api/src/modules/sales/preferences/service.ts:85-96
//       → 409 CONFLICT, details: { code: 'CONTACT_BLOCKED', ... }
//     - envio em andamento (Idempotency-Key "quente"):
//       apps/api/src/modules/conversations/idempotent-reservation.service.ts:135-139
//       → 409 CONFLICT, details: { code: 'IDEMPOTENCY_IN_PROGRESS' }
//
// LGPD (doc 17): o payload não contém PII — apenas UUID da simulação
//   e Idempotency-Key (IDs opacos).
// =============================================================================

import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '../../lib/api';

// ─── Tipos de resposta (espelho de SendSimulationResponseSchema do backend) ───

export interface SendSimulationResponse {
  /** 'sent' = enviado nesta requisição; 'already_sent' = Idempotency-Key reutilizada */
  status: 'sent' | 'already_sent';
  /** wamid retornado pela Meta, ou null se already_sent */
  sent_message_id: string | null;
}

// ─── Erros tipados ────────────────────────────────────────────────────────────

export type SendSimulationErrorCode =
  | 'FLAG_DISABLED' // 403 FEATURE_DISABLED — feature flag desligada
  | 'FORBIDDEN' // 403 FORBIDDEN — sem permissão (simulations:send)
  | 'NO_PHONE' // 422 details.code=lead_no_phone — lead sem telefone E.164
  | 'TEMPLATE_CATEGORY_NOT_ALLOWED' // 422 details.code=TEMPLATE_CATEGORY_NOT_ALLOWED
  | 'CONTACT_BLOCKED' // 409 details.code=CONTACT_BLOCKED — "não me contate"
  | 'SEND_IN_PROGRESS' // 409 details.code=IDEMPOTENCY_IN_PROGRESS — envio já em voo
  | 'META_UNAVAILABLE' // 502 — integração Meta não configurada / fora do ar
  | 'UNKNOWN';

export interface SendSimulationError {
  code: SendSimulationErrorCode;
  message: string;
}

/**
 * Extrai o código de domínio de `ApiError.details` (ver contrato no
 * cabeçalho deste arquivo). `details` é `unknown` por design — corpo de
 * resposta de rede, nunca confiar no shape sem checar.
 */
function domainErrorCode(details: unknown): string | null {
  if (details !== null && typeof details === 'object' && 'code' in details) {
    const value = (details as { code?: unknown }).code;
    return typeof value === 'string' ? value : null;
  }
  return null;
}

export function classifySendError(err: unknown): SendSimulationError {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      if (err.code === 'FEATURE_DISABLED' || err.code === 'FLAG_DISABLED') {
        return {
          code: 'FLAG_DISABLED',
          message: 'Envio de simulações está desativado no momento.',
        };
      }
      return { code: 'FORBIDDEN', message: 'Sem permissão para enviar simulações.' };
    }

    const domainCode = domainErrorCode(err.details);

    if (err.status === 409) {
      if (domainCode === 'CONTACT_BLOCKED') {
        return {
          code: 'CONTACT_BLOCKED',
          message: 'Este cliente pediu para não ser contatado por WhatsApp.',
        };
      }
      if (domainCode === 'IDEMPOTENCY_IN_PROGRESS') {
        return {
          code: 'SEND_IN_PROGRESS',
          message: 'Já existe um envio desta simulação em andamento. Aguarde alguns segundos.',
        };
      }
      // 409 desconhecido — não presume nada, usa a mensagem real do servidor.
      return {
        code: 'UNKNOWN',
        message: err.message || 'Não foi possível enviar a simulação.',
      };
    }

    if (err.status === 422) {
      if (domainCode === 'lead_no_phone') {
        return {
          code: 'NO_PHONE',
          message: 'Este lead não possui telefone cadastrado. Adicione um número para continuar.',
        };
      }
      if (domainCode === 'TEMPLATE_CATEGORY_NOT_ALLOWED') {
        return {
          code: 'TEMPLATE_CATEGORY_NOT_ALLOWED',
          message:
            'O cliente ainda não conversou com o Banco do Povo por este WhatsApp. A simulação ' +
            'só pode ser enviada a quem já escreveu antes.',
        };
      }
      // Qualquer outro 422 (ex.: IDEMPOTENCY_KEY_BODY_MISMATCH, contagem de
      // variáveis do template, ou código futuro do backend) — NUNCA "sem
      // telefone". Usa a mensagem real do servidor (achado de produção,
      // 17/09/2026: lead tinha telefone, recusa era outro motivo).
      return {
        code: 'UNKNOWN',
        message: err.message || 'Não foi possível enviar a simulação.',
      };
    }

    if (err.status === 502) {
      return {
        code: 'META_UNAVAILABLE',
        message:
          'Integração WhatsApp (Meta) indisponível no momento. Verifique a configuração ou tente novamente mais tarde.',
      };
    }
  }
  return {
    code: 'UNKNOWN',
    message: err instanceof Error ? err.message : 'Erro desconhecido ao enviar simulação.',
  };
}

// ─── Fetcher ──────────────────────────────────────────────────────────────────

interface SendSimulationParams {
  simulationId: string;
  /** UUID do canal de envio. null = usar canal padrão da organização. */
  channelId?: string | null;
}

async function postSendSimulation(params: SendSimulationParams): Promise<SendSimulationResponse> {
  const idempotencyKey = crypto.randomUUID();
  const body: Record<string, unknown> = {};
  if (params.channelId) {
    body.channelId = params.channelId;
  }
  return api.post<SendSimulationResponse>(
    `/api/simulations/${encodeURIComponent(params.simulationId)}/send`,
    body,
    { headers: { 'idempotency-key': idempotencyKey } },
  );
}

// ─── Callbacks ────────────────────────────────────────────────────────────────

interface UseSendSimulationCallbacks {
  onSuccess?: (data: SendSimulationResponse) => void;
  onError?: (error: SendSimulationError) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Mutation de envio de simulação por WhatsApp (F14-S06, atualizado F20-S07).
 *
 * Uso:
 *   const { send, isPending, sendError, reset } = useSendSimulation({
 *     onSuccess: (data) => toast('Simulação enviada!'),
 *     onError: (err) => toast(err.message, 'danger'),
 *   });
 *   send(simulationId);                       // usa canal padrão
 *   send(simulationId, channelId);            // usa canal específico (F20-S07)
 *
 * - Gera Idempotency-Key (UUID v4) automaticamente a cada chamada.
 * - isPending: true enquanto a requisição está em voo (use para desabilitar botão).
 * - sendError: erro tipado para UX específica por código (null quando sem erro).
 * - reset: limpa o estado de erro (use após fechar banner de erro).
 * - channelId opcional: null/undefined = canal padrão da org.
 */
export function useSendSimulation(callbacks?: UseSendSimulationCallbacks): {
  send: (simulationId: string, channelId?: string | null) => void;
  isPending: boolean;
  sendError: SendSimulationError | null;
  reset: () => void;
} {
  const { mutate, isPending, error, reset } = useMutation<
    SendSimulationResponse,
    unknown,
    SendSimulationParams
  >({
    mutationFn: postSendSimulation,
    onSuccess: (data) => {
      callbacks?.onSuccess?.(data);
    },
    onError: (err) => {
      callbacks?.onError?.(classifySendError(err));
    },
  });

  const sendError: SendSimulationError | null = error ? classifySendError(error) : null;

  return {
    send: (simulationId: string, channelId?: string | null) =>
      mutate({ simulationId, channelId: channelId ?? null }),
    isPending,
    sendError,
    reset,
  };
}
